/**
 * FPR Teams Tab Component
 *
 * Phase-aware team management for Flashpoint Race:
 *
 *   pre_registration  → info card (teams not yet available)
 *   pool_creation     → Pool assignment + leader selection (drag-drop pools)
 *   team_creation     → Step 2: fill teams via draft (manual / random / snake)
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import { Modal } from '@swtorbit/ui/react';
import { useGameContext } from '../../contexts/GameContext';
import {
  SocketEvent,
  FprTeam,
  FprCreateTeamResponse,
  FprUpdateTeamResponse,
  FprDeleteTeamResponse,
  FprLockTeamsResponse,
  FprUnlockTeamsResponse,
  FprJoinTeamResponse,
  FprLeaveTeamResponse,
  FPR_TEAM_SIZE,
  isTeamComplete,
  Player,
  GameType,
  type FprSnakeDraftState,
  type FprRegistrationRole,
  type GamePlayerInfo,
  getCurrentPicker,
  type FprCompositionViolationEntry,
  type FprTeamCompositionViolationBroadcast,
  type FprTeamCompositionClearedBroadcast,
  type PlayerCompositionInfo,
  type TogglePlayerRequest,
  isFprRoundPlayer,
} from '@swtorbit/shared-socket-events';
import { getFprRoleIconUrl } from '@swtorbit/shared-assets';
import type { GameStatus } from '@swtorbit/shared-socket-events';
import { PoolCreationPanel } from './PoolCreationPanel';

// ============================================================================
// Role Icon Helper
// ============================================================================

function RoleIcon({ role }: { role: FprRegistrationRole }) {
  return <img src={getFprRoleIconUrl(role)} alt={role} style={{ width: 32, height: 32, objectFit: 'contain' }} />;
}

// ============================================================================
// Helper Components
// ============================================================================

interface TeamCardProps {
  team: FprTeam;
  gamePlayers: Player[];
  teamsLocked: boolean;
  onDelete: (teamId: string) => void;
  onRename: (teamId: string, newName: string) => void;
  onLeave?: (teamId: string, playerId: string) => void;
  onToggle?: (playerId: string) => void;
  onMakeLead?: (teamId: string, playerId: string) => void;
  snakeDraftState?: FprSnakeDraftState | null;
  compositionViolations?: FprCompositionViolationEntry[];
  playerCompositionInfos?: Record<string, PlayerCompositionInfo>;
  roleAssignments?: Record<string, FprRegistrationRole>;
}

interface MemberDetail {
  playerId: string;
  characterName: string;
  isConnected: boolean;
  fprRole: FprRegistrationRole;
  role: 'lead' | 'member';
}

interface MemberRowProps {
  m: MemberDetail;
  teamId: string;
  teamsLocked: boolean;
  tlAssignedRole?: FprRegistrationRole;
  comp?: PlayerCompositionInfo;
  onLeave?: (teamId: string, playerId: string) => void;
  onToggle?: (playerId: string) => void;
  onMakeLead?: (teamId: string, playerId: string) => void;
}

function MemberRow({ m, teamId, teamsLocked, tlAssignedRole, comp, onLeave, onToggle, onMakeLead }: MemberRowProps) {
  return (
    <div
      className="d-flex align-items-center gap-2xs p-2xs rounded-base"
      style={{ backgroundColor: 'color-mix(in srgb, var(--color-white) 5%, transparent)' }}
    >
      <span className={`fs-xs ${m.isConnected ? 'text-green-dark' : 'text-red'}`}>
        {m.isConnected ? '●' : '○'}
      </span>
      {m.role === 'lead'
        ? (tlAssignedRole ? <RoleIcon role={tlAssignedRole} /> : <span>👑</span>)
        : <RoleIcon role={m.fprRole} />}
      <span className="text-white fs-sm" style={{ flex: 1 }}>{m.characterName}</span>
      {comp && (
        <span className="text-slate fs-xs">{comp.combatStyle} – {comp.discipline}</span>
      )}
      {m.role === 'lead' && <span title="Team lead">👑</span>}
      {!teamsLocked && (onLeave || onToggle || onMakeLead) && (
        <div className="d-flex gap-2xs">
          {m.role !== 'lead' && onMakeLead && (
            <button
              className="btn btn--outline btn--slate btn--sm"
              title="Promote to team lead"
              onClick={() => onMakeLead(teamId, m.playerId)}
            >👑</button>
          )}
          {m.role !== 'lead' && onLeave && (
            <button
              className="btn btn--outline btn--slate btn--sm"
              title="Remove from team"
              onClick={() => onLeave(teamId, m.playerId)}
            >↩</button>
          )}
          {onToggle && (
            <button
              className="btn btn--red btn--sm"
              title="Disable player"
              onClick={() => onToggle(m.playerId)}
            >🚫</button>
          )}
        </div>
      )}
    </div>
  );
}

function TeamCard({ team, gamePlayers, teamsLocked, onDelete, onRename, onLeave, onToggle, onMakeLead, snakeDraftState, compositionViolations, playerCompositionInfos, roleAssignments }: TeamCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(team.name);

  const memberDetails = useMemo<MemberDetail[]>(() => {
    return team.members.map((member) => {
      const player = gamePlayers.find((p) => p.playerId === member.playerId);
      const memberPick = snakeDraftState?.completedPicks.find(pick => pick.playerId === member.playerId);
      return {
        playerId: member.playerId,
        role: member.role,
        characterName: member.characterName || player?.characterName || 'Unknown',
        isConnected: player?.status === 'connected',
        fprRole: (memberPick?.neededRole ?? roleAssignments?.[member.playerId] ?? player?.fprRegistration?.roles?.[0] ?? 'dps') as FprRegistrationRole,
      };
    });
  }, [team.members, gamePlayers, snakeDraftState?.completedPicks, roleAssignments]);

  const memberCount = team.members.length;

  const handleSave = useCallback(() => {
    if (editName.trim() && editName !== team.name) {
      onRename(team.id, editName.trim());
    }
    setIsEditing(false);
  }, [editName, team.id, team.name, onRename]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSave();
      else if (e.key === 'Escape') { setEditName(team.name); setIsEditing(false); }
    },
    [handleSave, team.name]
  );

  return (
    <div
      className="bg-space bg-o-50 border-md border-blue-light rounded-base p-sm d-flex flex-column gap-xs"
      style={{ flex: '1 1 280px', minWidth: '260px', maxWidth: '420px' }}
    >
      <div className="d-flex justify-content-between align-items-center gap-xs flex-wrap">
        {isEditing && !teamsLocked ? (
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            autoFocus
            className="input"
            style={{ flex: 1, minWidth: '120px' }}
          />
        ) : (
          <h4
            className="m-0"
            style={{ cursor: teamsLocked ? 'default' : 'pointer' }}
            onClick={() => !teamsLocked && setIsEditing(true)}
          >
            {team.name}{' '}
            {!teamsLocked && <span className="fs-xs text-slate">✏️</span>}
          </h4>
        )}
        <div className="d-flex gap-2xs align-items-center">
          <span className="badge badge--sm">{memberCount}/{FPR_TEAM_SIZE}</span>
          {team.status === 'locked' && <span className="badge">🔒</span>}
          {team.status === 'ready' && <span className="badge badge--green">✅</span>}
          {team.status === 'forming' && <span className="badge badge--amber">⏳</span>}
          {!teamsLocked && (
            <button
              className="btn btn--red btn--sm"
              onClick={() => onDelete(team.id)}
              title="Delete team"
            >🗑️</button>
          )}
        </div>
      </div>

      <div className="d-flex flex-column gap-2xs">
        {memberDetails.map((m) => (
          <MemberRow
            key={m.playerId}
            m={m}
            teamId={team.id}
            teamsLocked={teamsLocked}
            tlAssignedRole={m.role === 'lead' ? snakeDraftState?.teamLeadAssignedRoles?.[m.playerId] : undefined}
            comp={playerCompositionInfos?.[m.playerId]}
            onLeave={onLeave}
            onToggle={onToggle}
            onMakeLead={onMakeLead}
          />
        ))}
        {Array.from({ length: FPR_TEAM_SIZE - memberCount }).map((_, i) => (
          <div
            key={`empty-${i}`}
            className="d-flex align-items-center gap-2xs p-2xs rounded-base"
            style={{ opacity: 0.4, backgroundColor: 'color-mix(in srgb, var(--color-white) 3%, transparent)' }}
          >
            <span>➕</span>
            <span className="fs-sm text-slate">Empty Slot</span>
          </div>
        ))}
      </div>

      {compositionViolations && compositionViolations.length > 0 && (
        <div className="bg-space border-md border-amber rounded-base p-xs">
          <span className="text-amber fw-bold fs-sm">⚠ Composition Violations</span>
          {compositionViolations.map((v, i) => (
            <div key={i} className="text-amber fs-sm mt-2xs">{v.message}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// DraftDashboard Component
// ============================================================================

interface DraftDashboardProps {
  players: GamePlayerInfo[];
  teams: FprTeam[];
  roleAssignments: Record<string, FprRegistrationRole>;
}

function DraftDashboard({ players, teams, roleAssignments }: DraftDashboardProps) {
  const available = players.filter(p => p.enabled !== false);
  const withReg = available.filter(p => p.fprRegistration);
  const missingReg = available.filter(p => !p.fprRegistration);
  const assignedIds = new Set(teams.flatMap(t => t.members.map(m => m.playerId)));
  const assigned = available.filter(p => assignedIds.has(p.playerId));
  const unassigned = available.filter(p => !assignedIds.has(p.playerId));
  const connectedCount = available.filter(p => p.isConnected).length;
  const hasAssignments = Object.keys(roleAssignments).length > 0;
  const tanks = hasAssignments
    ? withReg.filter(p => roleAssignments[p.playerId] === 'tank')
    : withReg.filter(p => p.fprRegistration!.roles.includes('tank'));
  const healers = hasAssignments
    ? withReg.filter(p => roleAssignments[p.playerId] === 'healer')
    : withReg.filter(p => p.fprRegistration!.roles.includes('healer'));
  const dps = hasAssignments
    ? withReg.filter(p => roleAssignments[p.playerId] === 'dps')
    : withReg.filter(p => p.fprRegistration!.roles.includes('dps'));
  const leaders = withReg.filter(p => p.fprRegistration!.availableAsLeader);

  return (
    <div className="bg-space bg-o-50 border-md border-blue-light rounded-base p-sm mb-sm">
      <h4 className="mb-xs">Draft Overview</h4>
      <div className="d-flex gap-xs flex-wrap mb-xs">
        <span className="badge badge--green">🟢 Available: {available.length}</span>
        <span className="badge badge--blue">📡 Live: {connectedCount}</span>
        <span className="badge badge--amber">📋 Registered: {withReg.length}</span>
        {missingReg.length > 0 && (
          <span className="badge badge--red">⚠ Missing: {missingReg.length}</span>
        )}
        <span className="badge badge--blue">✅ Assigned: {assigned.length}</span>
        {unassigned.length > 0 && (
          <span className="badge badge--slate">⏳ Unassigned: {unassigned.length}</span>
        )}
      </div>
      <div className="d-flex gap-xs flex-wrap">
        <span className="badge badge--aqua d-flex align-items-center gap-2xs"><RoleIcon role="tank" />Tanks: {tanks.length}</span>
        <span className="badge badge--green d-flex align-items-center gap-2xs"><RoleIcon role="healer" />Healers: {healers.length}</span>
        <span className="badge d-flex align-items-center gap-2xs"><RoleIcon role="dps" />DPS: {dps.length}</span>
        <span className="badge badge--amber">👑 Leaders: {leaders.length}</span>
      </div>
    </div>
  );
}

// ============================================================================
// Manual Team Builder — Drag-and-drop board for Manual draft mode
// ============================================================================

interface PlayerDragCardProps {
  playerId: string;
  index: number;
  player?: GamePlayerInfo;
  isLead: boolean;
  isDragDisabled: boolean;
  compositionInfo?: PlayerCompositionInfo;
  /** Authoritative role assigned via pool/draft state — falls back to first registered role */
  assignedRole?: FprRegistrationRole;
  inTeam: boolean;
  onMakeLead?: () => void;
  onLeave?: () => void;
  onToggle?: () => void;
}

function PlayerDragCard({
  playerId, index, player, isLead, isDragDisabled,
  compositionInfo, assignedRole, inTeam,
  onMakeLead, onLeave, onToggle,
}: PlayerDragCardProps) {
  const name = player?.characterName || playerId.slice(-8);
  const isConnected = player?.isConnected ?? false;
  const reg = player?.fprRegistration;
  const displayRole = assignedRole ?? reg?.roles?.[0] ?? 'dps';

  return (
    <Draggable draggableId={playerId} index={index} isDragDisabled={isDragDisabled}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`d-flex align-items-center gap-2xs p-2xs rounded-base ${snapshot.isDragging ? 'border-md border-blue-light' : ''}`}
          style={{
            ...provided.draggableProps.style,
            backgroundColor: snapshot.isDragging ? 'rgba(21, 167, 230, 0.15)' : 'rgba(255,255,255,0.05)',
            marginBottom: '4px',
            cursor: isDragDisabled ? 'default' : 'grab',
          }}
        >
          <span className={`fs-xs ${isConnected ? 'text-green-dark' : 'text-red'}`}>
            {isConnected ? '●' : '○'}
          </span>
          {isLead && <span title="Team lead">👑</span>}
          <span className="text-white fs-sm" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {name}
          </span>
          <div className="d-flex align-items-center gap-2xs">
            <RoleIcon role={displayRole} />
            {reg?.knowsMmMechanics && <span className="fs-xs text-amber" title="Knows MM mechanics">MM</span>}
            {reg?.availableAsLeader && !isLead && <span className="fs-xs" title="Available as leader">👑</span>}
          </div>
          {compositionInfo && (
            <span className="text-slate fs-xs" style={{ whiteSpace: 'nowrap' }}>
              {compositionInfo.combatStyle} – {compositionInfo.discipline}
            </span>
          )}
          {!isDragDisabled && (onMakeLead || onLeave || onToggle) && (
            <div className="d-flex gap-2xs">
              {inTeam && !isLead && onMakeLead && (
                <button
                  className="btn btn--outline btn--slate btn--sm"
                  title="Promote to team lead"
                  onClick={(e) => { e.stopPropagation(); onMakeLead(); }}
                >👑</button>
              )}
              {inTeam && !isLead && onLeave && (
                <button
                  className="btn btn--outline btn--slate btn--sm"
                  title="Remove from team"
                  onClick={(e) => { e.stopPropagation(); onLeave(); }}
                >↩</button>
              )}
              {onToggle && (
                <button
                  className="btn btn--red btn--sm"
                  title="Disable player"
                  onClick={(e) => { e.stopPropagation(); onToggle(); }}
                >🚫</button>
              )}
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
}

interface TeamColumnProps {
  droppableId: string;
  title: string;
  subtitle?: string;
  team?: FprTeam;
  playerIds: string[];
  leadPlayerId?: string;
  players: GamePlayerInfo[];
  teamsLocked: boolean;
  compositionInfos: Record<string, PlayerCompositionInfo>;
  roleAssignments: Record<string, FprRegistrationRole>;
  onDeleteTeam?: () => void;
  onRenameTeam?: (name: string) => void;
  onMakeLead?: (playerId: string) => void;
  onLeave?: (playerId: string) => void;
  onToggle?: (playerId: string) => void;
}

function TeamColumn({
  droppableId, title, subtitle, team, playerIds, leadPlayerId,
  players, teamsLocked, compositionInfos, roleAssignments,
  onDeleteTeam, onRenameTeam, onMakeLead, onLeave, onToggle,
}: TeamColumnProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(title);
  const isFull = team ? playerIds.length >= FPR_TEAM_SIZE : false;

  const handleSave = useCallback(() => {
    if (editName.trim() && editName !== title && onRenameTeam) {
      onRenameTeam(editName.trim());
    }
    setIsEditing(false);
  }, [editName, title, onRenameTeam]);

  return (
    <div
      className="bg-space bg-o-50 border-md border-blue-light rounded-base p-xs d-flex flex-column gap-2xs"
      style={{ flex: '1 1 240px', minWidth: '240px', maxWidth: '360px' }}
    >
      <div className="d-flex justify-content-between align-items-center gap-2xs flex-wrap">
        {isEditing && team && !teamsLocked ? (
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              else if (e.key === 'Escape') { setEditName(title); setIsEditing(false); }
            }}
            autoFocus
            className="input"
            style={{ flex: 1, minWidth: '120px' }}
          />
        ) : (
          <span
            className="text-white fw-bold"
            style={{ flex: 1, cursor: team && !teamsLocked ? 'pointer' : 'default' }}
            onClick={() => team && !teamsLocked && setIsEditing(true)}
          >
            {title}{team && !teamsLocked && <span className="fs-xs text-slate"> ✏️</span>}
          </span>
        )}
        <div className="d-flex gap-2xs align-items-center">
          <span className="badge badge--sm">
            {team ? `${playerIds.length}/${FPR_TEAM_SIZE}` : playerIds.length}
          </span>
          {team?.status === 'locked' && <span className="badge">🔒</span>}
          {team?.status === 'ready' && <span className="badge badge--green">✅</span>}
          {team?.status === 'forming' && <span className="badge badge--amber">⏳</span>}
          {team && !teamsLocked && onDeleteTeam && (
            <button className="btn btn--red btn--sm" title="Delete team" onClick={onDeleteTeam}>🗑️</button>
          )}
        </div>
      </div>
      {subtitle && <span className="text-slate fs-xs">{subtitle}</span>}

      <Droppable droppableId={droppableId} isDropDisabled={isFull || teamsLocked}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="rounded-base p-2xs d-flex flex-column"
            style={{
              minHeight: '120px',
              flex: 1,
              backgroundColor: snapshot.isDraggingOver
                ? 'rgba(21, 167, 230, 0.10)'
                : 'rgba(0, 0, 0, 0.2)',
              border: `1px solid ${snapshot.isDraggingOver ? '#15a7e6' : 'rgba(255,255,255,0.1)'}`,
              transition: 'background-color 0.2s, border-color 0.2s',
            }}
          >
            {playerIds.map((id, index) => {
              const player = players.find(p => p.playerId === id);
              const isLead = leadPlayerId === id;
              return (
                <PlayerDragCard
                  key={id}
                  playerId={id}
                  index={index}
                  player={player}
                  isLead={isLead}
                  isDragDisabled={teamsLocked || isLead}
                  compositionInfo={compositionInfos[id]}
                  assignedRole={roleAssignments[id]}
                  inTeam={!!team}
                  onMakeLead={onMakeLead ? () => onMakeLead(id) : undefined}
                  onLeave={onLeave ? () => onLeave(id) : undefined}
                  onToggle={onToggle ? () => onToggle(id) : undefined}
                />
              );
            })}
            {provided.placeholder}
            {playerIds.length === 0 && (
              <div className="fs-xs text-slate p-2xs" style={{ textAlign: 'center' }}>
                {team ? 'Drop players here' : 'All players assigned'}
              </div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
}

interface ManualTeamBuilderPanelProps {
  teams: FprTeam[];
  players: GamePlayerInfo[];
  teamsLocked: boolean;
  playerCompositionInfos: Record<string, PlayerCompositionInfo>;
  roleAssignments: Record<string, FprRegistrationRole>;
  onJoin: (teamId: string, playerId: string) => void;
  onLeave: (teamId: string, playerId: string) => void;
  onToggle: (playerId: string) => void;
  onMakeLead: (teamId: string, playerId: string) => void;
  onDeleteTeam: (teamId: string) => void;
  onRenameTeam: (teamId: string, name: string) => void;
  onCreateTeam: () => void;
}

function ManualTeamBuilderPanel({
  teams, players, teamsLocked, playerCompositionInfos, roleAssignments,
  onJoin, onLeave, onToggle, onMakeLead, onDeleteTeam, onRenameTeam, onCreateTeam,
}: ManualTeamBuilderPanelProps) {
  const [warning, setWarning] = useState<string | null>(null);

  const assignedSet = useMemo(
    () => new Set(teams.flatMap(t => t.members.map(m => m.playerId))),
    [teams]
  );
  const unassignedIds = useMemo(
    () => players.filter(p => p.enabled !== false && !assignedSet.has(p.playerId)).map(p => p.playerId),
    [players, assignedSet]
  );

  const handleDragEnd = useCallback((result: DropResult) => {
    setWarning(null);
    if (!result.destination || teamsLocked) return;
    const playerId = result.draggableId;
    const srcId = result.source.droppableId;
    const dstId = result.destination.droppableId;
    if (srcId === dstId) return;

    const srcTeamId = srcId.startsWith('team:') ? srcId.slice(5) : null;
    const dstTeamId = dstId.startsWith('team:') ? dstId.slice(5) : null;

    if (srcTeamId) {
      const srcTeam = teams.find(t => t.id === srcTeamId);
      if (srcTeam?.leadPlayerId === playerId) {
        setWarning('Team leads cannot be dragged. Use "Promote to team lead" on another member, or delete the team.');
        return;
      }
    }
    if (dstTeamId) {
      const dstTeam = teams.find(t => t.id === dstTeamId);
      if (dstTeam && dstTeam.members.length >= FPR_TEAM_SIZE) {
        setWarning(`${dstTeam.name} is already full.`);
        return;
      }
    }

    if (srcTeamId) onLeave(srcTeamId, playerId);
    if (dstTeamId) {
      if (srcTeamId) setTimeout(() => onJoin(dstTeamId, playerId), 0);
      else onJoin(dstTeamId, playerId);
    }
  }, [teams, teamsLocked, onJoin, onLeave]);

  if (teams.length === 0) {
    return (
      <div className="bg-space bg-o-50 border-md border-amber rounded-base p-sm">
        <p className="text-amber fs-sm mb-xs">
          No teams found. Confirming pool leaders in the previous step normally scaffolds teams automatically.
          Create one manually if needed.
        </p>
        <button className="btn" onClick={onCreateTeam}>➕ Create Team</button>
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="d-flex flex-column gap-xs">
        {warning && (
          <div className="bg-space bg-o-50 border-md border-amber rounded-base p-xs">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-amber fs-sm">⚠ {warning}</span>
              <button className="btn btn--outline btn--slate btn--sm" onClick={() => setWarning(null)}>✕</button>
            </div>
          </div>
        )}

        <div className="d-flex gap-sm flex-wrap">
          <TeamColumn
            droppableId="unassigned"
            title="Unassigned"
            subtitle={`${unassignedIds.length} available`}
            playerIds={unassignedIds}
            players={players}
            teamsLocked={teamsLocked}
            compositionInfos={playerCompositionInfos}
            roleAssignments={roleAssignments}
            onToggle={onToggle}
          />

          {teams.map(team => (
            <TeamColumn
              key={team.id}
              droppableId={`team:${team.id}`}
              title={team.name}
              team={team}
              playerIds={team.members.map(m => m.playerId)}
              leadPlayerId={team.leadPlayerId}
              players={players}
              teamsLocked={teamsLocked}
              compositionInfos={playerCompositionInfos}
              roleAssignments={roleAssignments}
              onDeleteTeam={() => onDeleteTeam(team.id)}
              onRenameTeam={(name) => onRenameTeam(team.id, name)}
              onMakeLead={(pid) => onMakeLead(team.id, pid)}
              onLeave={(pid) => onLeave(team.id, pid)}
              onToggle={onToggle}
            />
          ))}
        </div>
      </div>
    </DragDropContext>
  );
}

// ============================================================================
// Snake Draft Observer View
// ============================================================================

interface SnakeDraftObserverProps {
  state: FprSnakeDraftState;
  teams: FprTeam[];
  gamePlayers: Player[];
  neededRole: FprRegistrationRole;
  onForceAutoPickk: () => void;
  onForcePick: (playerId: string) => void;
}

function SnakeDraftObserver({ state, teams, gamePlayers, neededRole, onForceAutoPickk, onForcePick }: SnakeDraftObserverProps) {
  const [countdown, setCountdown] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const tick = () => {
      setCountdown(Math.max(0, Math.ceil((state.pickDeadline - Date.now()) / 1000)));
    };
    tick();
    intervalRef.current = setInterval(tick, 500);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [state.pickDeadline]);

  const currentPickerId = getCurrentPicker(state);
  const currentPicker = gamePlayers.find(p => p.playerId === currentPickerId);
  const pickerName = currentPicker?.characterName || currentPickerId.slice(-8);

  const getPlayerName = (id: string) =>
    gamePlayers.find(p => p.playerId === id)?.characterName || id.slice(-8);

  const roundDir = state.roundNumber % 2 === 1 ? 'Forward' : 'Reversed';

  return (
    <div className="d-flex flex-column gap-sm">
      <div className="bg-space bg-o-50 border-md border-blue-light rounded-base p-sm">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-xs">
          <div>
            <span className="text-white fs-sm">Now picking: </span>
            <span className="fw-bold text-white">{pickerName}</span>
            <span className="text-white fs-sm ms-xs">Round {state.roundNumber} ({roundDir})</span>
          </div>
          <div className="d-flex align-items-center gap-xs">
            <span className={`fw-bold ${countdown <= 10 ? 'text-red' : 'text-amber'}`}>
              ⏱ {countdown}s
            </span>
            <button className="btn btn--outline btn--slate" onClick={onForceAutoPickk} title="Auto-pick best available player">
              ⏩ Auto-Pick
            </button>
          </div>
        </div>
      </div>

      <div className="d-flex gap-sm flex-wrap">
        <div className="d-flex flex-column gap-xs" style={{ flex: '1', minWidth: '200px' }}>
          <h4>Teams</h4>
          {teams.map(team => {
            const isCurrentPicker = team.leadPlayerId === currentPickerId;
            return (
              <div
                key={team.id}
                className={`team-card forming ${isCurrentPicker ? 'complete' : ''}`}
                style={isCurrentPicker ? { borderColor: 'var(--color-blue-light)', boxShadow: '0 0 8px var(--color-blue-light)' } : {}}
              >
                <div className="team-header">
                  <h4 className="team-name">{team.name} {isCurrentPicker ? '← Picking' : ''}</h4>
                </div>
                <div className="team-members">
                  {team.members.map(m => {
                    const memberPick = state.completedPicks.find(pick => pick.playerId === m.playerId);
                    const fprRole = (memberPick?.neededRole ?? 'dps') as FprRegistrationRole;
                    const tlAssignedRole = m.role === 'lead' ? state.teamLeadAssignedRoles?.[m.playerId] : undefined;
                    return (
                      <div key={m.playerId} className={`team-member ${m.role === 'lead' ? 'lead' : ''}`}>
                        <span className="member-role">
                          {m.role === 'lead'
                            ? <>{tlAssignedRole ? <RoleIcon role={tlAssignedRole} /> : '👑'}</>
                            : <RoleIcon role={fprRole} />}
                        </span>
                        <span className="fw-medium text-green">{m.characterName || getPlayerName(m.playerId)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="d-flex flex-column gap-xs" style={{ flex: '2', minWidth: '240px' }}>
          <div className="d-flex align-items-center gap-xs">
            <h4 style={{ margin: 0 }}>Available Players</h4>
            <span className={`badge badge--sm ${neededRole === 'tank' ? 'badge--blue' : neededRole === 'healer' ? 'badge--green' : 'badge--amber'}`}>
              Now picking: {neededRole.charAt(0).toUpperCase() + neededRole.slice(1)}
            </span>
          </div>
          {[
            { icon: <RoleIcon role="tank" />, label: 'Tanks', pool: state.tankPool, role: 'tank' as FprRegistrationRole },
            { icon: <RoleIcon role="healer" />, label: 'Healers', pool: state.healerPool, role: 'healer' as FprRegistrationRole },
            { icon: <RoleIcon role="dps" />, label: 'DPS', pool: state.dpsPool, role: 'dps' as FprRegistrationRole },
          ].map(({ icon, label, pool, role }) => pool.length > 0 && (
            <div key={label} className="bg-space bg-o-50 border-md border-blue-light rounded-base p-xs" style={{ opacity: role === neededRole ? 1 : 0.4 }}>
              <span className="d-flex align-items-center gap-2xs text-white fs-sm fw-bold">{icon}{label}</span>
              <div className="d-flex flex-column gap-2xs mt-2xs">
                {pool.map(id => (
                  <div key={id} className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2xs">
                      <RoleIcon role={role} />
                      <span className="text-white fs-sm">{getPlayerName(id)}</span>
                    </div>
                    <button
                      className="btn"
                      style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                      onClick={() => onForcePick(id)}
                      disabled={role !== neededRole}
                      title={role !== neededRole ? `Team needs a ${neededRole}, not a ${role}` : `Force pick ${getPlayerName(id)} for ${pickerName}`}
                    >
                      Pick
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// TL Role Assignment Panel — Step 2b (snake draft only)
// ============================================================================

interface TLLeadInfo {
  playerId: string;
  characterName: string;
  registeredRoles: FprRegistrationRole[];
  assignedRole: FprRegistrationRole;
}

const ROLE_LABEL: Record<FprRegistrationRole, string> = {
  tank: 'Tank',
  healer: 'Healer',
  dps: 'DPS',
};

interface TLRoleAssignmentPanelProps {
  leadInfo: TLLeadInfo[];
  assignments: Record<string, FprRegistrationRole>;
  warnings: string[];
  isComputed: boolean;
  isLoading: boolean;
  teamsExist: boolean;
  onCompute: () => void;
  onOverride: (playerId: string, role: FprRegistrationRole) => void;
  onConfirmStart: () => void;
}

function TLRoleAssignmentPanel({
  leadInfo, assignments, warnings, isComputed, isLoading, teamsExist,
  onCompute, onOverride, onConfirmStart,
}: TLRoleAssignmentPanelProps) {
  return (
    <div className="bg-space bg-o-50 border-md border-blue-light rounded-base p-sm mt-xs">
      <div className="d-flex justify-content-between align-items-center mb-xs">
        <h4 style={{ margin: 0 }}>Step 2b — Confirm TL Roles</h4>
        <button
          className="btn btn--outline btn--slate"
          onClick={onCompute}
          disabled={isLoading || !teamsExist}
          title={!teamsExist ? 'Teams with leaders must exist first' : 'Compute optimal role assignments'}
        >
          {isLoading ? 'Computing…' : isComputed ? '🔄 Recompute' : '⚙ Compute Assignments'}
        </button>
      </div>

      {!isComputed && (
        <p className="text-white fs-sm">
          Click &quot;Compute Assignments&quot; to calculate the optimal role for each Team Leader before starting the draft.
          You can override any assignment before confirming.
        </p>
      )}

      {isComputed && warnings.length > 0 && (
        <div className="bg-space bg-o-50 border-md rounded-base p-xs mb-xs" style={{ borderColor: 'var(--color-bronze)' }}>
          {warnings.map((w, i) => (
            <p key={i} className="fs-sm" style={{ color: 'var(--color-amber)', margin: i > 0 ? '4px 0 0' : 0 }}>⚠ {w}</p>
          ))}
        </div>
      )}

      {isComputed && leadInfo.length > 0 && (
        <>
          <div className="d-flex flex-column gap-2xs mb-sm">
            {leadInfo.map(lead => (
              <div key={lead.playerId} className="d-flex align-items-center gap-xs flex-wrap">
                <span className="text-white fs-sm fw-bold" style={{ minWidth: '120px' }}>
                  {lead.characterName}
                </span>
                <div className="d-flex gap-2xs">
                  {lead.registeredRoles.map(r => (
                    <span key={r} className="badge badge--sm text-white d-flex align-items-center gap-2xs" style={{ fontSize: '0.7rem' }}>
                      <RoleIcon role={r} />{ROLE_LABEL[r]}
                    </span>
                  ))}
                </div>
                <span className="text-white fs-sm">→</span>
                <select
                  value={assignments[lead.playerId] ?? lead.assignedRole}
                  onChange={e => onOverride(lead.playerId, e.target.value as FprRegistrationRole)}
                  className="input"
                  style={{ width: '120px', padding: '2px 6px', fontSize: '0.85rem' }}
                >
                  {lead.registeredRoles.map(r => (
                    <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <button
            className="btn"
            onClick={onConfirmStart}
            disabled={isLoading}
          >
            {isLoading ? 'Starting…' : 'Confirm & Start Draft'}
          </button>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Main TeamsTab Component
// ============================================================================

interface TeamsTabProps {
  players: GamePlayerInfo[];
}

export function TeamsTab({ players }: TeamsTabProps) {
  const { socket } = useSocketContext();
  const { selectedGame } = useGameContext();

  const [teams, setTeams] = useState<FprTeam[]>([]);
  const [teamsLocked, setTeamsLocked] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Draft UI state
  const [draftMode, setDraftMode] = useState<'manual' | 'random' | 'snake'>('manual');
  const [timePerPick, setTimePerPick] = useState(30);
  const [draftWarnings, setDraftWarnings] = useState<string[]>([]);
  const [tlAssignments, setTlAssignments] = useState<Record<string, FprRegistrationRole>>({});
  const [tlLeadInfo, setTlLeadInfo] = useState<TLLeadInfo[]>([]);
  const [tlRoleWarnings, setTlRoleWarnings] = useState<string[]>([]);
  const [tlRolesComputed, setTlRolesComputed] = useState(false);
  const [snakeDraftState, setSnakeDraftState] = useState<FprSnakeDraftState | null>(
    selectedGame?.fprSnakeDraftState ?? null
  );
  const [violationsByTeam, setViolationsByTeam] = useState<Record<string, FprCompositionViolationEntry[]>>({});

  const enabledPlayers = useMemo(() => players.filter(p => p.enabled !== false), [players]);

  const playerCompositionInfos = useMemo((): Record<string, PlayerCompositionInfo> => {
    if (!selectedGame?.rounds?.length) return {};
    const lastRound = selectedGame.rounds[selectedGame.rounds.length - 1];
    const result: Record<string, PlayerCompositionInfo> = {};
    for (const p of (lastRound?.players ?? [])) {
      if (isFprRoundPlayer(p) && p.compositionInfo) {
        result[p.playerId] = p.compositionInfo;
      }
    }
    return result;
  }, [selectedGame?.rounds]);
  const gameStatus = selectedGame?.status;
  const isPoolCreationPhase = gameStatus === 'pool_creation';
  const isTeamCreationPhase = gameStatus === 'team_creation';

  // Sync teams from game state
  useEffect(() => {
    if (selectedGame) {
      setTeams(selectedGame.fprTeams || []);
      setTeamsLocked(selectedGame.fprTeamsLocked || false);
    }
  }, [selectedGame?.fprTeams, selectedGame?.fprTeamsLocked]);

  // Sync snake draft state from game
  useEffect(() => {
    setSnakeDraftState(selectedGame?.fprSnakeDraftState ?? null);
  }, [selectedGame?.fprSnakeDraftState]);

  const availablePlayers = useMemo(() => {
    const teamPlayerIds = new Set(teams.flatMap((t) => t.members.map((m) => m.playerId)));
    return enabledPlayers.filter((p) => !teamPlayerIds.has(p.playerId));
  }, [enabledPlayers, teams]);

  const neededRole = useMemo((): FprRegistrationRole => {
    if (!snakeDraftState || snakeDraftState.status !== 'in_progress') return 'dps';
    const currentPickerId = getCurrentPicker(snakeDraftState);
    const tlRole = snakeDraftState.teamLeadAssignedRoles?.[currentPickerId];
    const teamPicks = snakeDraftState.completedPicks.filter(p => p.teamLeadId === currentPickerId);
    const hasTank = tlRole === 'tank' || teamPicks.some(p => p.neededRole === 'tank');
    const hasHealer = tlRole === 'healer' || teamPicks.some(p => p.neededRole === 'healer');
    if (!hasTank) return 'tank';
    if (!hasHealer) return 'healer';
    return 'dps';
  }, [snakeDraftState]);

  // ============================================================================
  // Socket Event Listeners
  // ============================================================================

  useEffect(() => {
    if (!socket) return;

    const handleTeamCreated = (data: FprCreateTeamResponse) => {
      if (data.success && data.team) {
        setTeams((prev) => [...prev, data.team!]);
        setShowCreateModal(false);
        setSelectedLeadId('');
        setError(null);
      } else {
        setError(data.error || 'Failed to create team');
      }
      setIsLoading(false);
    };

    const handleTeamUpdated = (data: FprUpdateTeamResponse) => {
      if (!data.success) { setError(data.error || 'Failed to update team'); return; }
      if (data.team) setTeams((prev) => prev.map((t) => (t.id === data.team!.id ? data.team! : t)));
      setError(null);
    };

    // Player join/leave broadcasts fire on drag-drop in the manual builder.
    // Why: server persists the move but only emits these events — without listeners
    // the UI snaps the card back until a manual reload.
    const handlePlayerJoinedTeam = (data: FprJoinTeamResponse) => {
      if (!data.success) { setError(data.error || 'Failed to add player to team'); return; }
      if (data.team) setTeams((prev) => prev.map((t) => (t.id === data.team!.id ? data.team! : t)));
      setError(null);
    };

    const handlePlayerLeftTeam = (data: FprLeaveTeamResponse) => {
      if (!data.success) { setError(data.error || 'Failed to remove player from team'); return; }
      if (data.team) setTeams((prev) => prev.map((t) => (t.id === data.team!.id ? data.team! : t)));
      setError(null);
    };

    const handleTeamDeleted = (data: FprDeleteTeamResponse) => {
      if (data.success && data.teamId) {
        setTeams((prev) => prev.filter((t) => t.id !== data.teamId));
        setError(null);
      } else {
        setError(data.error || 'Failed to delete team');
      }
    };

    const handleTeamsLocked = (data: FprLockTeamsResponse) => {
      if (data.success) {
        setTeamsLocked(true);
        if (data.teams) setTeams(data.teams);
        setError(null);
      } else {
        setError(data.error || 'Failed to lock teams');
      }
    };

    const handleTeamsUnlocked = (data: FprUnlockTeamsResponse) => {
      if (data.success) {
        setTeamsLocked(false);
        if (data.teams) setTeams(data.teams);
        setError(null);
      } else {
        setError(data.error || 'Failed to unlock teams');
      }
    };

    const handleRandomDraftResult = (data: { success: boolean; teams?: FprTeam[]; warnings?: string[]; game?: { fprTeams?: FprTeam[] }; error?: string }) => {
      if (data.success) {
        if (data.game?.fprTeams) setTeams(data.game.fprTeams);
        else if (data.teams) setTeams(data.teams);
        setDraftWarnings(data.warnings ?? []);
        setError(null);
      } else {
        setError(data.error ?? 'Randomisation failed');
      }
      setIsLoading(false);
    };

    const handleSnakeDraftStarted = (data: { success: boolean; state?: FprSnakeDraftState; warnings?: string[]; error?: string }) => {
      if (data.success && data.state) {
        setSnakeDraftState(data.state);
        setDraftWarnings(data.warnings ?? []);
        setError(null);
      } else {
        setError(data.error ?? 'Failed to start snake draft');
      }
      setIsLoading(false);
    };

    const handleSnakeDraftState = (data: { state?: FprSnakeDraftState | null; cancelled?: boolean; success?: boolean; error?: string }) => {
      if (data.error) setError(data.error);
      if (data.state !== undefined) setSnakeDraftState(data.state);
    };

    const handleTlRolesComputed = (data: {
      success: boolean;
      assignments?: Record<string, FprRegistrationRole>;
      warnings?: string[];
      leadInfo?: TLLeadInfo[];
      error?: string;
    }) => {
      if (data.success && data.assignments && data.leadInfo) {
        setTlAssignments(data.assignments);
        setTlLeadInfo(data.leadInfo);
        setTlRoleWarnings(data.warnings ?? []);
        setTlRolesComputed(true);
        setError(null);
      } else {
        setError(data.error ?? 'Failed to compute TL role assignments');
      }
      setIsLoading(false);
    };

    const handleCompositionViolation = (data: FprTeamCompositionViolationBroadcast) => {
      setViolationsByTeam((prev) => ({ ...prev, [data.teamId]: data.violations }));
    };

    const handleCompositionCleared = (data: FprTeamCompositionClearedBroadcast) => {
      setViolationsByTeam((prev) => {
        const next = { ...prev };
        delete next[data.teamId];
        return next;
      });
    };

    socket.on(SocketEvent.FPR_TEAM_CREATED, handleTeamCreated);
    socket.on(SocketEvent.FPR_TEAM_UPDATED, handleTeamUpdated);
    socket.on(SocketEvent.FPR_PLAYER_JOINED_TEAM, handlePlayerJoinedTeam);
    socket.on(SocketEvent.FPR_PLAYER_LEFT_TEAM, handlePlayerLeftTeam);
    socket.on(SocketEvent.FPR_TEAM_DELETED, handleTeamDeleted);
    socket.on(SocketEvent.FPR_TEAMS_LOCKED, handleTeamsLocked);
    socket.on(SocketEvent.FPR_TEAMS_UNLOCKED, handleTeamsUnlocked);
    socket.on(SocketEvent.FPR_RANDOM_DRAFT_RESULT, handleRandomDraftResult);
    socket.on(SocketEvent.FPR_SNAKE_DRAFT_STARTED, handleSnakeDraftStarted);
    socket.on(SocketEvent.FPR_SNAKE_DRAFT_STATE, handleSnakeDraftState);
    socket.on(SocketEvent.FPR_TL_ROLES_COMPUTED, handleTlRolesComputed);
    socket.on(SocketEvent.FPR_TEAM_COMPOSITION_VIOLATION, handleCompositionViolation);
    socket.on(SocketEvent.FPR_TEAM_COMPOSITION_CLEARED, handleCompositionCleared);

    return () => {
      socket.off(SocketEvent.FPR_TEAM_CREATED, handleTeamCreated);
      socket.off(SocketEvent.FPR_TEAM_UPDATED, handleTeamUpdated);
      socket.off(SocketEvent.FPR_PLAYER_JOINED_TEAM, handlePlayerJoinedTeam);
      socket.off(SocketEvent.FPR_PLAYER_LEFT_TEAM, handlePlayerLeftTeam);
      socket.off(SocketEvent.FPR_TEAM_DELETED, handleTeamDeleted);
      socket.off(SocketEvent.FPR_TEAMS_LOCKED, handleTeamsLocked);
      socket.off(SocketEvent.FPR_TEAMS_UNLOCKED, handleTeamsUnlocked);
      socket.off(SocketEvent.FPR_RANDOM_DRAFT_RESULT, handleRandomDraftResult);
      socket.off(SocketEvent.FPR_SNAKE_DRAFT_STARTED, handleSnakeDraftStarted);
      socket.off(SocketEvent.FPR_SNAKE_DRAFT_STATE, handleSnakeDraftState);
      socket.off(SocketEvent.FPR_TL_ROLES_COMPUTED, handleTlRolesComputed);
      socket.off(SocketEvent.FPR_TEAM_COMPOSITION_VIOLATION, handleCompositionViolation);
      socket.off(SocketEvent.FPR_TEAM_COMPOSITION_CLEARED, handleCompositionCleared);
    };
  }, [socket]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleCreateTeam = useCallback(() => {
    if (!socket || !selectedGame || !selectedLeadId) return;
    setIsLoading(true);
    setError(null);
    const lead = enabledPlayers.find((p) => p.playerId === selectedLeadId);
    socket.emit(SocketEvent.FPR_CREATE_TEAM, {
      roomCode: selectedGame.roomCode,
      leadPlayerId: selectedLeadId,
      teamName: `${lead?.characterName || 'Team'}'s Team`,
    });
  }, [socket, selectedGame, selectedLeadId, enabledPlayers]);

  const handleDeleteTeam = useCallback((teamId: string) => {
    if (!socket || !selectedGame) return;
    socket.emit(SocketEvent.FPR_DELETE_TEAM, { roomCode: selectedGame.roomCode, teamId });
  }, [socket, selectedGame]);

  const handleRenameTeam = useCallback((teamId: string, newName: string) => {
    if (!socket || !selectedGame) return;
    socket.emit(SocketEvent.FPR_UPDATE_TEAM, { roomCode: selectedGame.roomCode, teamId, name: newName });
  }, [socket, selectedGame]);

  const handleLockTeams = useCallback(() => {
    if (!socket || !selectedGame) return;
    socket.emit(SocketEvent.FPR_LOCK_TEAMS, { roomCode: selectedGame.roomCode });
  }, [socket, selectedGame]);

  const handleUnlockTeams = useCallback(() => {
    if (!socket || !selectedGame) return;
    socket.emit(SocketEvent.FPR_UNLOCK_TEAMS, { roomCode: selectedGame.roomCode });
  }, [socket, selectedGame]);

  const handleJoinTeam = useCallback((teamId: string, playerId: string) => {
    if (!socket || !selectedGame) return;
    socket.emit(SocketEvent.FPR_JOIN_TEAM, { roomCode: selectedGame.roomCode, teamId, playerId });
  }, [socket, selectedGame]);

  const handleLeaveTeam = useCallback((teamId: string, playerId: string) => {
    if (!socket || !selectedGame) return;
    socket.emit(SocketEvent.FPR_LEAVE_TEAM, { roomCode: selectedGame.roomCode, teamId, playerId });
  }, [socket, selectedGame]);

  const handleTogglePlayer = useCallback((playerId: string) => {
    if (!socket || !selectedGame) return;
    const player = players.find(p => p.playerId === playerId);
    if (!player) return;
    const request: TogglePlayerRequest = {
      roomCode: selectedGame.roomCode,
      playerId,
      enabled: !(player.enabled !== false),
    };
    socket.emit(SocketEvent.ADMIN_TOGGLE_PLAYER, request);
  }, [socket, selectedGame, players]);

  // Promote a member to team lead by recreating the team with the new lead.
  // Why: there is no dedicated FPR_REASSIGN_LEAD event yet; admin re-adds members via drag.
  const handleMakeLead = useCallback((teamId: string, newLeadPlayerId: string) => {
    if (!socket || !selectedGame) return;
    const newLead = players.find(p => p.playerId === newLeadPlayerId);
    socket.emit(SocketEvent.FPR_DELETE_TEAM, { roomCode: selectedGame.roomCode, teamId });
    setTimeout(() => {
      socket.emit(SocketEvent.FPR_CREATE_TEAM, {
        roomCode: selectedGame.roomCode,
        leadPlayerId: newLeadPlayerId,
        teamName: `${newLead?.characterName ?? 'Team'}'s Team`,
      });
    }, 0);
  }, [socket, selectedGame, players]);

  const handleStartRandomDraft = useCallback(() => {
    if (!socket || !selectedGame) return;
    setIsLoading(true);
    setDraftWarnings([]);
    setError(null);
    socket.emit(SocketEvent.FPR_START_RANDOM_DRAFT, { roomCode: selectedGame.roomCode });
  }, [socket, selectedGame]);

  const handleStartSnakeDraft = useCallback(() => {
    if (!socket || !selectedGame) return;
    setIsLoading(true);
    setError(null);
    socket.emit(SocketEvent.FPR_CONFIGURE_DRAFT, {
      roomCode: selectedGame.roomCode,
      config: { mode: 'snake', timePerPickSeconds: timePerPick },
    });
    socket.emit(SocketEvent.FPR_START_SNAKE_DRAFT, {
      roomCode: selectedGame.roomCode,
      tlRoleAssignments: tlAssignments,
    });
  }, [socket, selectedGame, timePerPick, tlAssignments]);

  const handleComputeTlRoles = useCallback(() => {
    if (!socket || !selectedGame) return;
    setIsLoading(true);
    setTlRolesComputed(false);
    setError(null);
    socket.emit(SocketEvent.FPR_COMPUTE_TL_ROLES, { roomCode: selectedGame.roomCode });
  }, [socket, selectedGame]);

  const handleTlRoleOverride = useCallback((playerId: string, role: FprRegistrationRole) => {
    setTlAssignments(prev => ({ ...prev, [playerId]: role }));
  }, []);

  // When team leaders change, pre-populate TL role assignments from pool state (if available)
  // so the admin can go straight to "Confirm & Start" without clicking "Compute TL Roles"
  const teamLeadKey = teams.map(t => t.leadPlayerId).join(',');
  useEffect(() => {
    const poolLeaderRoles = selectedGame?.fprPoolState?.leaderRoles;
    if (teams.length > 0 && poolLeaderRoles && Object.keys(poolLeaderRoles).length > 0) {
      const leadInfo: TLLeadInfo[] = teams.map(t => {
        const player = players.find(p => p.playerId === t.leadPlayerId);
        return {
          playerId: t.leadPlayerId,
          characterName: player?.characterName ?? t.leadPlayerId.slice(-8),
          registeredRoles: player?.fprRegistration?.roles ?? ['dps'],
          assignedRole: poolLeaderRoles[t.leadPlayerId] ?? 'dps',
        };
      });
      setTlLeadInfo(leadInfo);
      setTlAssignments(poolLeaderRoles);
      setTlRolesComputed(true);
      setTlRoleWarnings([]);
    } else {
      setTlRolesComputed(false);
      setTlAssignments({});
      setTlLeadInfo([]);
      setTlRoleWarnings([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamLeadKey, selectedGame?.fprPoolState?.leaderRoles]);

  const handleForceAutoPick = useCallback(() => {
    if (!socket || !selectedGame) return;
    socket.emit(SocketEvent.FPR_SKIP_SNAKE_PICK, { roomCode: selectedGame.roomCode });
  }, [socket, selectedGame]);

  const handleForcePick = useCallback((playerId: string) => {
    if (!socket || !selectedGame) return;
    socket.emit(SocketEvent.FPR_SNAKE_DRAFT_PICK, { roomCode: selectedGame.roomCode, playerId });
  }, [socket, selectedGame]);

  const handleCancelSnakeDraft = useCallback(() => {
    if (!socket || !selectedGame) return;
    socket.emit(SocketEvent.FPR_CANCEL_SNAKE_DRAFT, { roomCode: selectedGame.roomCode });
  }, [socket, selectedGame]);

  const handleBackToPoolCreation = useCallback(() => {
    if (!socket || !selectedGame) return;
    const confirmed = window.confirm(
      'Going back will delete teams and cancel any active draft. Pool assignments will be preserved. Continue?',
    );
    if (!confirmed) return;
    socket.emit(SocketEvent.ADMIN_UPDATE_GAME_STATUS, {
      roomCode: selectedGame.roomCode,
      status: 'pool_creation' as GameStatus,
    });
  }, [socket, selectedGame]);

  const roleAssignments = useMemo<Record<string, FprRegistrationRole>>(
    () => selectedGame?.fprPoolState?.roleAssignments ?? {},
    [selectedGame?.fprPoolState?.roleAssignments],
  );

  // ============================================================================
  // Render
  // ============================================================================

  if (!selectedGame) {
    return (
      <div className="text-white">
        <h2>Teams</h2>
        <p>Select a game from the dropdown above to manage teams.</p>
      </div>
    );
  }

  if (selectedGame.gameType !== GameType.FP_RACE) {
    return (
      <div className="text-white">
        <h2>Teams</h2>
        <p>Teams are only available for Flashpoint Race games.</p>
      </div>
    );
  }

  const allTeamsReady = teams.length > 0 && teams.every((t) => isTeamComplete(t));
  const canLockTeams = allTeamsReady && !teamsLocked;
  const snakeDraftActive = snakeDraftState?.status === 'in_progress';

  // ── pre_registration: teams not yet available ──────────────────────────────
  if (gameStatus === 'pre_registration' || gameStatus === 'draft') {
    return (
      <div className="text-white">
        <h2>Teams</h2>
        <div className="bg-space bg-o-50 border-md border-blue-light rounded-base p-lg">
          <p className="text-white">
            📨 Registrations are currently open. Close registrations and begin <strong>Pool Creation</strong> to start setting up teams.
          </p>
          <p className="text-white fs-sm mt-xs">
            The Teams tab becomes active once you advance the game lifecycle to the Pool Creation phase.
          </p>
        </div>
      </div>
    );
  }

  // ── pool_creation: Pool assignment + leader selection ──────────────────────
  if (isPoolCreationPhase) {
    return (
      <div className="text-white">
        <h2>Teams — Pool Creation</h2>

        {error && (
          <div className="teams-error d-flex justify-content-between align-items-center mb-xs">
            <span>{error}</span>
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}

        <PoolCreationPanel players={players} />
      </div>
    );
  }

  // ── team_creation: Step 2 (existing draft UI) ──────────────────────────────
  return (
    <div className="text-white">
      <div className="d-flex justify-content-between align-items-center gap-xs flex-wrap mb-xs">
        <h2 className="m-0">Teams — Step 2: Draft</h2>
        {isTeamCreationPhase && (
          <button
            className="btn btn--outline btn--slate"
            onClick={handleBackToPoolCreation}
            title="Return to pool creation — teams will be deleted, pool assignments preserved"
          >
            ← Back to Pool Creation
          </button>
        )}
      </div>

      {isTeamCreationPhase && players.length > 0 && (
        <DraftDashboard players={players} teams={teams} roleAssignments={roleAssignments} />
      )}

      {error && (
        <div className="teams-error d-flex justify-content-between align-items-center mb-xs">
          <span>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {draftWarnings.length > 0 && (
        <div className="bg-space bg-o-50 border-md border-amber rounded-base p-sm mb-sm">
          <div className="d-flex justify-content-between align-items-center mb-2xs">
            <span className="text-amber fw-bold">⚠ Draft Warnings</span>
            <button className="btn btn--outline btn--slate" style={{ padding: '2px 8px', fontSize: '0.75rem' }} onClick={() => setDraftWarnings([])}>✕</button>
          </div>
          {draftWarnings.map((w, i) => (
            <div key={i} className="text-amber fs-sm">{w}</div>
          ))}
        </div>
      )}

      {isTeamCreationPhase && !snakeDraftActive && (
        <div className="bg-space bg-o-50 border-md border-blue-light rounded-base p-sm mb-sm">
          <h4 className="mb-xs">Draft Mode</h4>
          <div className="d-flex gap-xs mb-sm flex-wrap">
            {(['manual', 'random', 'snake'] as const).map(mode => (
              <button
                key={mode}
                className={`btn ${draftMode === mode ? '' : 'btn--outline btn--slate'}`}
                onClick={() => setDraftMode(mode)}
              >
                {mode === 'manual' ? '✋ Manual' : mode === 'random' ? '🎲 Randomise' : 'Snake Draft'}
              </button>
            ))}
          </div>

          {draftMode === 'random' && (
            <div className="d-flex align-items-center gap-xs">
              <button
                className="btn"
                onClick={handleStartRandomDraft}
                disabled={isLoading || enabledPlayers.length < 2}
              >
                {isLoading ? 'Generating…' : '🎲 Generate Teams'}
              </button>
              <span className="text-white fs-sm">
                Automatically assigns {enabledPlayers.length} players into balanced teams
              </span>
            </div>
          )}

          {draftMode === 'snake' && (
            <>
              <div className="d-flex align-items-center gap-2xs">
                <label className="text-white fs-sm">Seconds per pick:</label>
                <input
                  type="number"
                  min={10}
                  max={120}
                  value={timePerPick}
                  onChange={e => setTimePerPick(Math.min(120, Math.max(10, Number(e.target.value))))}
                  className="input"
                  style={{ width: '70px' }}
                />
              </div>
              {teams.length === 0 && (
                <span className="text-amber fs-sm">
                  {selectedGame.fprPoolState?.status === 'confirmed'
                    ? 'Teams will be initialized from pool leaders when the draft starts.'
                    : 'No teams found — go back to Leader Selection'}
                </span>
              )}
              <TLRoleAssignmentPanel
                leadInfo={tlLeadInfo}
                assignments={tlAssignments}
                warnings={tlRoleWarnings}
                isComputed={tlRolesComputed}
                isLoading={isLoading}
                teamsExist={teams.length > 0}
                onCompute={handleComputeTlRoles}
                onOverride={handleTlRoleOverride}
                onConfirmStart={handleStartSnakeDraft}
              />
            </>
          )}
        </div>
      )}

      {snakeDraftActive && (
        <div className="d-flex justify-content-between align-items-center mb-sm">
          <span className="text-amber fw-bold">🐍 Snake Draft in Progress</span>
          <button className="btn btn--red" onClick={handleCancelSnakeDraft}>Cancel Draft</button>
        </div>
      )}

      {snakeDraftActive && snakeDraftState && (
        <SnakeDraftObserver
          state={snakeDraftState}
          teams={teams}
          gamePlayers={selectedGame.players}
          neededRole={neededRole}
          onForceAutoPickk={handleForceAutoPick}
          onForcePick={handleForcePick}
        />
      )}

      {snakeDraftState?.status === 'completed' && (
        <div className="bg-space bg-o-50 border-md border-green-dark rounded-base p-sm mb-sm">
          <span className="text-green-dark fw-bold">✅ Snake Draft Complete — {snakeDraftState.completedPicks.length} picks made</span>
        </div>
      )}

      {(!snakeDraftActive) && (
        <>
          <div className="teams-status-bar">
            <div className="teams-summary">
              <span className="team-count">{teams.length} team{teams.length !== 1 ? 's' : ''}</span>
              <span className="player-count">
                {teams.reduce((acc, t) => acc + t.members.length, 0)}/{teams.length * FPR_TEAM_SIZE} players assigned
              </span>
            </div>
            <div className="teams-controls">
              {!teamsLocked && draftMode === 'manual' && teams.length > 0 && (
                <button
                  className="create-team-btn"
                  onClick={() => setShowCreateModal(true)}
                  disabled={availablePlayers.length === 0}
                  title={availablePlayers.length === 0 ? 'No available players for team lead' : 'Create a new team'}
                >
                  ➕ Create Team
                </button>
              )}
              {teamsLocked ? (
                <button className="unlock-teams-btn" onClick={handleUnlockTeams}>🔓 Unlock Teams</button>
              ) : (
                <button
                  className="lock-teams-btn"
                  onClick={handleLockTeams}
                  disabled={!canLockTeams}
                  title={!canLockTeams ? 'All teams must be complete to lock' : 'Lock all teams'}
                >
                  🔒 Lock Teams
                </button>
              )}
            </div>
          </div>

          {draftMode === 'manual' ? (
            <ManualTeamBuilderPanel
              teams={teams}
              players={enabledPlayers}
              teamsLocked={teamsLocked}
              playerCompositionInfos={playerCompositionInfos}
              roleAssignments={roleAssignments}
              onJoin={handleJoinTeam}
              onLeave={handleLeaveTeam}
              onToggle={handleTogglePlayer}
              onMakeLead={handleMakeLead}
              onDeleteTeam={handleDeleteTeam}
              onRenameTeam={handleRenameTeam}
              onCreateTeam={() => setShowCreateModal(true)}
            />
          ) : (
            <div className="d-flex gap-sm flex-wrap">
              {teams.length === 0 ? (
                <div className="no-teams">
                  <p>No teams found. Use the draft controls above.</p>
                </div>
              ) : (
                teams.map((team) => (
                  <TeamCard
                    key={team.id}
                    team={team}
                    gamePlayers={selectedGame.players}
                    teamsLocked={teamsLocked}
                    onDelete={handleDeleteTeam}
                    onRename={handleRenameTeam}
                    onLeave={handleLeaveTeam}
                    onToggle={handleTogglePlayer}
                    onMakeLead={handleMakeLead}
                    snakeDraftState={snakeDraftState}
                    compositionViolations={violationsByTeam[team.id]}
                    playerCompositionInfos={playerCompositionInfos}
                    roleAssignments={roleAssignments}
                  />
                ))
              )}
            </div>
          )}
        </>
      )}

      <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)} className="create-team-modal" title="Select Team Lead" footer={
<>
          <button className="cancel-btn" onClick={() => setShowCreateModal(false)}>Cancel</button>
          <button
            className="create-btn"
            onClick={handleCreateTeam}
            disabled={!selectedLeadId || isLoading}
          >
            {isLoading ? 'Creating...' : 'Create Team'}
          </button>
        </>
}>
          <div className="field">
            <label htmlFor="team-lead">Team Lead</label>
            <select
              className="select"
              id="team-lead"
              value={selectedLeadId}
              onChange={(e) => setSelectedLeadId(e.target.value)}
            >
              <option value="">Select a team lead...</option>
              {availablePlayers.map((player) => (
                <option key={player.playerId} value={player.playerId}>
                  {player.characterName || 'Unknown'} {player.isConnected ? '🟢' : '🔴'}
                </option>
              ))}
            </select>
          </div>
        </Modal>
    </div>
  );
}

export default TeamsTab;
